import { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { FONT_COLOR, FONT_VARIANT } from '../../../../../components/fonts/font-props';
import FontBody2 from '../../../../../components/fonts/font-body2/font-body2';
import SearchInput from '../../../../../components/search-input/search-input';

import { OrderEligibilityDisplayService, OrderEligibilityResponse } from './order-eligibility-display.service';
import OrderStatusBanner from './components/order-status-banner';
import OrderItemsAnalysis from './components/order-items-analysis/order-items-analysis';
import EligibilitySummary from './components/eligibility-summary';
import PolicyRulesAnalysis from './components/policy-rules-analysis/policy-rules-analysis';

import './ui/custom-collapsible/custom-collapsible.scss';
import './order-eligibility-display.scss';


interface OrderEligibilityDisplayProps {
    data: OrderEligibilityResponse;
    orderName?: string;
}

const OrderEligibilityDisplay: React.FC<OrderEligibilityDisplayProps> = observer(({ data, orderName }) => {
    // Filtering state
    const [policyFilter, setPolicyFilter] = useState('');
    const [productFilter, setProductFilter] = useState('');

    // All hooks must be called before any early returns
    // Ensure required arrays exist
    const items = useMemo(() => data?.items || [], [data?.items]);
    
    // Calculate actual eligible items using service
    const eligibleItems = useMemo(() => 
        OrderEligibilityDisplayService.calculateEligibleItems(items),
        [items]
    );

    // Filtered data based on search terms using service
    const filteredItems = useMemo(() => 
        OrderEligibilityDisplayService.filterItemsByProduct(items, productFilter),
        [items, productFilter]
    );

    // Get all unique policies from filtered items using service
    const filteredPolicies = useMemo(() => 
        OrderEligibilityDisplayService.getFilteredPolicies(filteredItems),
        [filteredItems]
    );

    // Add defensive programming to prevent runtime errors - after all hooks
    if (!data) {
        return (
            <div className="order-eligibility-display">
                <FontBody2 color={FONT_COLOR.SEMANTIC_DANGER}>
                    No eligibility data available
                </FontBody2>
            </div>
        );
    }

    // Ensure order_eligibility exists
    if (!data.order_eligibility) {
        return (
            <div className="order-eligibility-display">
                <FontBody2 color={FONT_COLOR.SEMANTIC_DANGER}>
                    No order eligibility data available
                </FontBody2>
            </div>
        );
    }

    return (
        <div className="order-eligibility-display">
            <OrderStatusBanner 
                orderEligibility={data.order_eligibility}
                hasEligibleItems={eligibleItems.length > 0}
                orderName={orderName}
            />
            
            {/* Filtering Section */}
            <div className="filtering-section">
                <div className="filter-group">
                    <div className="filter-label">
                        <FontBody2 variant={[FONT_VARIANT.BOLD]}>
                            Filter by Product Name:
                        </FontBody2>
                    </div>
                    <SearchInput
                        placeholder="Search products..."
                        value={productFilter}
                        onChange={setProductFilter}
                        onClick={() => {}}
                    />
                </div>
                <div className="filter-group">
                    <div className="filter-label">
                        <FontBody2 variant={[FONT_VARIANT.BOLD]}>
                            Filter Policy Rules:
                        </FontBody2>
                    </div>
                    <SearchInput
                        placeholder="Filter policy rules in items..."
                        value={policyFilter}
                        onChange={setPolicyFilter}
                        onClick={() => {}}
                    />
                </div>
            </div>
            
            <EligibilitySummary 
                filteredItems={filteredItems}
                items={items}
            />
            <OrderItemsAnalysis 
                filteredItems={filteredItems}
                policyFilter={policyFilter}
            />
            <PolicyRulesAnalysis 
                filteredPolicies={filteredPolicies}
                filteredItems={filteredItems}
                policyFilter={policyFilter}
            />
        </div>
    );
});

export default OrderEligibilityDisplay;
